import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, FileText, Search, Plus, Briefcase, 
  Paperclip, X, UploadCloud, Filter, ChevronRight, LogIn, Download, Settings, UserPlus,
  Trash2, Edit, FolderOpen, Eye, AlertCircle, FileSpreadsheet, ArrowLeft, MessageSquare, Star, 
  Clock, CheckCircle, HelpCircle, Calendar, ShieldCheck, XCircle, Bell, User,
  CheckSquare, Lock, PlayCircle, Layout, CalendarDays, DollarSign, ListTodo, Save, File, QrCode
} from 'lucide-react';

const API_BASE_URL = '/api'; 

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString();
  } catch (e) {
    return 'Error';
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'Just now';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return 'Error';
  }
};

const LoginView = ({ onLogin, onVerify2FA, show2FA, qrCodeUrl, secret, loading, error }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');

  useEffect(() => {
    if (show2FA) {
      console.log("2FA View Active. QR Code URL:", qrCodeUrl ? "Present" : "Missing");
      console.log("Secret Key:", secret ? "Present" : "Missing");
    }
  }, [show2FA, qrCodeUrl, secret]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (show2FA) {
      onVerify2FA(otp);
    } else {
      onLogin(email, password);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md">
        <div className="flex justify-center mb-6">
           <img 
             src="/kqsolictor.jpg" 
             alt="KQ Solicitors" 
             className="w-32 h-auto object-contain" 
           />
        </div>
        <h2 className="text-2xl font-bold text-center text-slate-800 mb-2">KQ Solicitor Case Manager</h2>
        <p className="text-center text-slate-500 mb-8">Server Database Edition</p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {!show2FA ? (
            <>
              <input 
                type="email" 
                placeholder="Email" 
                required 
                className="w-full border border-slate-300 rounded p-2 focus:ring-2 focus:ring-slate-500 outline-none" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
              />
              <input 
                type="password" 
                placeholder="Password" 
                required 
                className="w-full border border-slate-300 rounded p-2 focus:ring-2 focus:ring-slate-500 outline-none" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
              />
            </>
          ) : (
            <div className="animate-in fade-in slide-in-from-right duration-300 flex flex-col items-center">
              <div className="bg-blue-50 border border-blue-200 text-blue-800 p-3 rounded mb-4 text-sm flex items-center gap-2 w-full justify-center">
                <Lock className="w-4 h-4" />
                Two-Step Verification
              </div>

              {/* QR Code Display Logic */}
              {qrCodeUrl ? (
                <div className="mb-6 text-center border-b border-slate-100 pb-6 w-full">
                  <p className="text-sm font-bold text-slate-700 mb-3">1. Scan this with Authenticator App</p>
                  <div className="bg-white p-2 border border-slate-200 rounded inline-block shadow-sm">
                    <img src={qrCodeUrl} alt="2FA QR Code" className="w-40 h-40" />
                  </div>
                  <div className="mt-4 bg-slate-50 p-3 rounded border border-slate-200">
                    <p className="text-xs text-slate-500 mb-1 uppercase font-bold">Or enter key manually:</p>
                    <p className="font-mono text-sm font-bold text-slate-800 break-all select-all">{secret}</p>
                  </div>
                </div>
              ) : (
                /* Fallback if QR code is missing but secret exists */
                secret ? (
                   <div className="mb-6 text-center border-b border-slate-100 pb-6 w-full">
                      <p className="text-sm font-bold text-slate-700 mb-2">Setup Key (No QR)</p>
                      <div className="bg-slate-50 p-3 rounded border border-slate-200">
                        <p className="font-mono text-sm font-bold text-slate-800 break-all select-all">{secret}</p>
                      </div>
                   </div>
                ) : (
                   <div className="mb-6 text-center text-slate-500 text-sm italic">
                      Use the code from your existing Authenticator App setup.
                   </div>
                )
              )}

              <div className="w-full">
                <p className="text-sm text-slate-600 mb-2 text-center">
                   {qrCodeUrl ? '2. Enter the 6-digit code generated' : 'Enter the 6-digit code from your app'}
                </p>
                <input 
                  type="text" 
                  placeholder="000000" 
                  required 
                  maxLength={6}
                  autoFocus
                  className="w-full border border-slate-300 rounded p-3 text-center text-2xl font-bold tracking-widest focus:ring-2 focus:ring-slate-500 outline-none" 
                  value={otp} 
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} 
                />
              </div>
            </div>
          )}

          {error && <div className="text-red-500 text-sm text-center bg-red-50 p-2 rounded border border-red-100">{error}</div>}
          
          <button type="submit" disabled={loading} className="w-full bg-slate-800 text-white py-3 rounded flex justify-center gap-2 hover:bg-slate-700 transition-colors">
            {loading ? 'Processing...' : (show2FA ? 'Verify Code' : <><LogIn className="w-5 h-5" /> Login</>)}
          </button>

          {show2FA && (
            <button 
              type="button" 
              onClick={() => window.location.reload()} 
              className="w-full text-center text-xs text-slate-500 hover:text-slate-800 mt-2 hover:underline"
            >
              Cancel / Return to Login
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default function App() {
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user_info') || 'null'));
  
  const [view, setView] = useState('portal'); 
  
  // 2FA State
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempAuthToken, setTempAuthToken] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [secretKey, setSecretKey] = useState(null);

  const [clients, setClients] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [activeViewers, setActiveViewers] = useState({}); 
  
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [missingDocFilter, setMissingDocFilter] = useState('None'); 
  const [statusFilter, setStatusFilter] = useState('All');
  const [dateFilter, setDateFilter] = useState('All');
  const [enteredByFilter, setEnteredByFilter] = useState('All'); 
  
  const [activeTab, setActiveTab] = useState('Overview'); 
  
  const [noteText, setNoteText] = useState('');
  const [noteImportant, setNoteImportant] = useState(false);
  const [noteSearch, setNoteSearch] = useState(''); 
  
  const [reminderTask, setReminderTask] = useState('');
  const [reminderDate, setReminderDate] = useState('');
  
  const [paymentAmount, setPaymentAmount] = useState('');
  const [bioForm, setBioForm] = useState({});
  const [keyDatesForm, setKeyDatesForm] = useState({});

  const statusOptions = [
    "File Opened",
    "Work in Progress",
    "Application Prepared — Not Yet Submitted",
    "Application Submitted to Home Office",
    "Awaiting Decision",
    "Decision Received",
    "File Closed"
  ];

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '', dob: '', fileDate: '', 
    caseType: 'Family Law', 
    caseStatus: 'File Opened', 
    idProofType: 'Passport', addressProofType: 'Utility Bill', 
    biometricAppointmentDate: '', biometricReminderDate: '', biometricNote: '',
    feeDecided: '',
    files: [] 
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [staffForm, setStaffForm] = useState({ id: null, name: '', email: '', password: '', role: 'Staff' });
  const [isEditingStaff, setIsEditingStaff] = useState(false);
  const [profileForm, setProfileForm] = useState({ currentPassword: '', newPassword: '' });

  const docCategories = [
    "General", 
    "LOA", 
    "AML Form", 
    "Client ID", 
    "Proof of Address",
    "Client Care", 
    "Instruction Letter", 
    "Client Application", 
    "Client Conversation", 
    "File Closing Letter/Email"
  ];

  const filterCategories = [
    "LOA", 
    "AML Form", 
    "Client ID",
    "Proof of Address",
    "Client Care", 
    "Instruction Letter", 
    "Client Application", 
    "Client Conversation", 
    "File Closing Letter/Email"
  ];

  const fetchClients = React.useCallback(() => {
    fetch(`${API_BASE_URL}/clients`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.status === 401 ? handleLogout() : res.json())
      .then(data => setClients(Array.isArray(data) ? data : []));
  }, [token]);

  const fetchUsers = React.useCallback(() => {
    if (user?.role === 'Admin') {
        fetch(`${API_BASE_URL}/users`, { headers: { 'Authorization': `Bearer ${token}` } })
          .then(res => res.json())
          .then(data => setUsersList(Array.isArray(data) ? data : []));
    }
  }, [token, user]);

  const fetchActiveViewers = React.useCallback(() => {
    fetch(`${API_BASE_URL}/active-viewers`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setActiveViewers(data))
      .catch(() => {});
  }, [token]);

  useEffect(() => { 
      if (token) {
          fetchClients();
          if (view === 'team') fetchUsers();
          const viewerInterval = setInterval(fetchActiveViewers, 5000);
          return () => clearInterval(viewerInterval);
      }
  }, [token, fetchClients, view, fetchUsers, fetchActiveViewers]);

  useEffect(() => {
    if (token && view === 'view-client' && selectedClient) {
      fetch(`${API_BASE_URL}/heartbeat/${selectedClient.id}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      const interval = setInterval(() => {
        fetch(`${API_BASE_URL}/heartbeat/${selectedClient.id}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [token, view, selectedClient]);


  useEffect(() => {
    if (selectedClient) {
      setBioForm({
        biometricAppointmentDate: selectedClient.biometricAppointmentDate || '',
        biometricReminderDate: selectedClient.biometricReminderDate || '',
        biometricNote: selectedClient.biometricNote || '',
        biometricReminderSent: selectedClient.biometricReminderSent === 1
      });
      setKeyDatesForm({
        keyClientInstructionDate: selectedClient.keyClientInstructionDate || '',
        keyAppSubmissionDate: selectedClient.keyAppSubmissionDate || '',
        keyBiometricDate: selectedClient.keyBiometricDate || '',
        keyDecisionReceivedDate: selectedClient.keyDecisionReceivedDate || '',
        keyDecisionSentDate: selectedClient.keyDecisionSentDate || '',
        keyFileClosingDate: selectedClient.keyFileClosingDate || ''
      });
    }
  }, [selectedClient]);

  const availableDates = useMemo(() => {
      const dates = new Set();
      clients.forEach(c => {
          if (c.fileDate) {
              try {
                const d = new Date(c.fileDate);
                if (!isNaN(d.getTime())) {
                   dates.add(d.toLocaleString('default', { month: 'long', year: 'numeric' }));
                }
              } catch(e) {}
          }
      });
      return Array.from(dates).sort();
  }, [clients]);

  const availableCreators = useMemo(() => {
      const creators = new Set();
      clients.forEach(c => {
          creators.add(c.creatorName || 'System');
      });
      return Array.from(creators).sort();
  }, [clients]);

  const biometricAlerts = useMemo(() => {
      const today = new Date();
      today.setHours(0,0,0,0);
      const nextWeek = new Date(today);
      nextWeek.setDate(today.getDate() + 7);

      const overdue = [];
      const dueToday = [];
      const upcoming = [];

      clients.forEach(c => {
          if (c.biometricReminderDate && !c.biometricReminderSent) {
              const reminderDate = new Date(c.biometricReminderDate);
              reminderDate.setHours(0,0,0,0);
              
              if (isNaN(reminderDate.getTime())) return;

              if (reminderDate < today) overdue.push(c);
              else if (reminderDate.getTime() === today.getTime()) dueToday.push(c);
              else if (reminderDate <= nextWeek) upcoming.push(c);
          }
      });
      return { overdue, dueToday, upcoming };
  }, [clients]);

  const totalPaid = useMemo(() => {
      if(!selectedClient || !selectedClient.payments) return 0;
      return selectedClient.payments.reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
  }, [selectedClient]);

  const statusCounts = useMemo(() => {
      return {
          opened: clients.filter(c => c.caseStatus === 'File Opened').length,
          wip: clients.filter(c => c.caseStatus === 'Work in Progress').length,
          awaiting: clients.filter(c => c.caseStatus === 'Awaiting Decision').length,
          decision: clients.filter(c => c.caseStatus === 'Decision Received').length
      };
  }, [clients]);

  const getStatusColor = (status) => {
    switch (status) {
      case "Decision Received": return "bg-green-100 text-green-800 border border-green-200";
      case "File Closed": return "bg-gray-100 text-gray-800 border border-gray-200";
      case "Awaiting Decision": return "bg-orange-100 text-orange-800 border border-orange-200";
      case "Application Submitted to Home Office": return "bg-blue-100 text-blue-800 border border-blue-200";
      case "File Opened": return "bg-purple-100 text-purple-800 border border-purple-200";
      case "Work in Progress": return "bg-amber-100 text-amber-800 border border-amber-200";
      case "Application Prepared — Not Yet Submitted": return "bg-indigo-100 text-indigo-800 border border-indigo-200";
      default: return "bg-slate-100 text-slate-700 border border-slate-200";
    }
  };

  const filteredClients = useMemo(() => clients.filter(c => {
    const name = (c.name || '').toLowerCase();
    const email = (c.email || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    const matchesSearch = name.includes(term) || email.includes(term);

    const matchesType = filterType === 'All' || c.caseType === filterType;
    const matchesStatus = statusFilter === 'All' || c.caseStatus === statusFilter;
    
    let matchesCreator = true;
    if (enteredByFilter !== 'All') {
        matchesCreator = (c.creatorName || 'System') === enteredByFilter;
    }

    let matchesDate = true;
    if (dateFilter !== 'All') {
        if (!c.fileDate) matchesDate = false;
        else {
            try {
                const d = new Date(c.fileDate);
                if (!isNaN(d.getTime())) {
                    matchesDate = (d.toLocaleString('default', { month: 'long', year: 'numeric' }) === dateFilter);
                } else matchesDate = false;
            } catch(e) { matchesDate = false; }
        }
    }
    
    let matchesMissingDoc = true;
    if (missingDocFilter !== 'None') {
        const hasDoc = c.files?.some(f => (f.category || 'General') === missingDocFilter);
        matchesMissingDoc = !hasDoc;
    }
    
    return matchesSearch && matchesType && matchesMissingDoc && matchesStatus && matchesDate && matchesCreator;
  }), [clients, searchTerm, filterType, missingDocFilter, statusFilter, dateFilter, enteredByFilter]);

  const handleLogin = async (email, password) => {
    setLoading(true);
    setAuthError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');

      if (data.requires2FA) {
        setRequires2FA(true);
        setTempAuthToken(data.tempToken);
        setQrCodeUrl(data.qrCodeUrl);
        setSecretKey(data.secret);
        setLoading(false);
        return;
      }

      localStorage.setItem('auth_token', data.token); 
      localStorage.setItem('user_info', JSON.stringify(data.user));
      setToken(data.token); 
      setUser(data.user);
      setView('portal'); 
    } catch (e) { 
      setAuthError(e.message); 
      setLoading(false); 
    } 
  };

  const handleVerify2FA = async (code) => {
    setLoading(true);
    setAuthError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/verify-2fa`, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify({ tempToken: tempAuthToken, code }) 
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.error || 'Verification failed');

      localStorage.setItem('auth_token', data.token); 
      localStorage.setItem('user_info', JSON.stringify(data.user));
      setToken(data.token); 
      setUser(data.user);
      setRequires2FA(false); 
      setQrCodeUrl(null); 
      setSecretKey(null);
      setView('portal'); 
    } catch (e) { 
      setAuthError(e.message); 
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear(); setToken(null); setUser(null); setView('portal'); setRequires2FA(false);
  };

  const uploadWithProgress = (url, formData) => {
      return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', url, true);
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          
          xhr.upload.onprogress = (e) => {
              if (e.lengthComputable) {
                  const percent = Math.round((e.loaded / e.total) * 100);
                  setUploadProgress(percent);
              }
          };

          xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText));
              else reject(xhr.statusText);
          };
          xhr.onerror = () => reject(xhr.statusText);
          xhr.send(formData);
      });
  };

  const handleDelete = async (id) => {
    if(!window.confirm("Delete case?")) return;
    try {
        await fetch(`${API_BASE_URL}/clients/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
        fetchClients();
    } catch (e) { alert("Delete failed or Access Denied"); }
  };

  const handleEdit = (client) => {
    setFormData({ ...client, files: [] });
    setIsEditing(true);
    setView('add-client');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true); setUploadProgress(0);
    try {
      if (isEditing) {
          await fetch(`${API_BASE_URL}/clients/${formData.id}`, { 
              method: 'PUT', 
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, 
              body: JSON.stringify(formData) 
          });
      } else {
          const payload = new FormData();
          const categories = [];
          Object.keys(formData).forEach(key => {
            if (key === 'files') {
                formData.files.forEach(f => {
                    payload.append('documents', f.file);
                    categories.push('General'); 
                });
            } else payload.append(key, formData[key]);
          });
          payload.append('fileCategories', JSON.stringify(categories));
          
          await uploadWithProgress(`${API_BASE_URL}/clients`, payload);
      }
      await fetchClients();
      resetForm();
      setView('dashboard');
    } catch (e) { alert("Error saving"); }
    finally { setIsUploading(false); }
  };

  const handleBiometricSave = async (e) => {
      e.preventDefault();
      try {
          const fullUpdateData = { ...selectedClient, ...bioForm };
          await fetch(`${API_BASE_URL}/clients/${selectedClient.id}`, { 
              method: 'PUT', 
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, 
              body: JSON.stringify(fullUpdateData)
          });
          setSelectedClient(fullUpdateData);
          setClients(clients.map(c => c.id === fullUpdateData.id ? fullUpdateData : c));
          alert("Biometric details saved!");
      } catch(e) { alert("Update failed"); }
  };

  const handleKeyDatesSave = async (e) => {
      e.preventDefault();
      try {
          const fullUpdateData = { ...selectedClient, ...keyDatesForm };
          await fetch(`${API_BASE_URL}/clients/${selectedClient.id}`, { 
              method: 'PUT', 
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, 
              body: JSON.stringify(fullUpdateData)
          });
          setSelectedClient(fullUpdateData);
          setClients(clients.map(c => c.id === fullUpdateData.id ? fullUpdateData : c));
          alert("Key Dates saved successfully!");
      } catch(e) { alert("Update failed"); }
  };

  const handleDownloadKeyDates = () => {
    if (!selectedClient) return;
    const content = `KEY DATES FOR CASE: ${selectedClient.name} (ID: ${selectedClient.id})
--------------------------------------------------
Client Visited / Instruction Date: ${formatDate(keyDatesForm.keyClientInstructionDate)}
Application Submission Date:       ${formatDate(keyDatesForm.keyAppSubmissionDate)}
Biometric Date:                    ${formatDate(keyDatesForm.keyBiometricDate)}
Decision Received Date:            ${formatDate(keyDatesForm.keyDecisionReceivedDate)}
Decision Sent Date:                ${formatDate(keyDatesForm.keyDecisionSentDate)}
File Closing Date:                 ${formatDate(keyDatesForm.keyFileClosingDate)}
--------------------------------------------------
Generated on: ${new Date().toLocaleString()}
`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `KeyDates_${selectedClient.id}.txt`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExport = () => {
      const headers = ["Client ID", "Name", "Email", "Phone", "Case Type", "Status", "Approval", "Entered By", "Address", "DOB", "File Date", "Fee Decided"];
      const csvContent = [
          headers.join(","),
          ...filteredClients.map(c => [
              c.id, `"${c.name}"`, c.email, c.phone, c.caseType, c.caseStatus, c.approvalStatus, `"${c.creatorName || 'System'}"`, `"${c.address}"`, c.dob, c.fileDate, c.feeDecided
          ].join(","))
      ].join("\n");
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "legal_clients.csv";
      link.click();
  };

  const handleDownloadNotes = () => {
    if (!selectedClient?.notes || selectedClient.notes.length === 0) {
      alert("No notes to download.");
      return;
    }
    const notesContent = selectedClient.notes.map(note => 
      `[${formatDateTime(note.date)}] ${note.author}${note.isImportant ? ' [IMPORTANT]' : ''}\n${note.text}\n`
    ).join('\n----------------------------------------\n\n');

    const blob = new Blob([notesContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Notes_Case_${selectedClient.id}.txt`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetForm = () => {
      setFormData({ 
          name: '', email: '', phone: '', address: '', dob: '', fileDate: '', 
          caseType: 'Family Law', 
          caseStatus: 'File Opened', 
          idProofType: 'Passport', addressProofType: 'Utility Bill', 
          biometricAppointmentDate: '', biometricReminderDate: '', biometricNote: '',
          feeDecided: '',
          files: [] 
      });
      setIsEditing(false);
  };

  const handleFileAdd = (e) => {
      const newFiles = Array.from(e.target.files).map(file => ({ file, category: 'General' }));
      setFormData({ ...formData, files: [...formData.files, ...newFiles] });
  };

  const handleDeleteFile = async (filename) => { if(!window.confirm("Delete doc?")) return; try { const res = await fetch(`${API_BASE_URL}/clients/${selectedClient.id}/files/${filename}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }); if(res.ok) { const updatedClients = clients.map(c => { if(c.id === selectedClient.id) { const newFiles = c.files.filter(f => f.filename !== filename); const updated = { ...c, files: newFiles }; setSelectedClient(updated); return updated; } return c; }); setClients(updatedClients); } } catch(e){} };
  
  const handleSingleUpload = async (e) => { 
      const file = e.target.files[0]; if (!file) return; 
      const uploadData = new FormData(); 
      uploadData.append('document', file); 
      uploadData.append('category', activeTab); 
      
      setIsUploading(true); setUploadProgress(0);
      try { 
          const data = await uploadWithProgress(`${API_BASE_URL}/clients/${selectedClient.id}/files`, uploadData);
          const updatedClients = clients.map(c => {
             if(c.id === selectedClient.id) {
                 const newFiles = [...c.files, data.file];
                 const updated = { ...c, files: newFiles };
                 setSelectedClient(updated); return updated;
             } return c;
          });
          setClients(updatedClients);
          alert("Upload Successful!"); 
      } catch (err) { alert("Upload Failed"); } 
      finally { setIsUploading(false); }
  };

  const handleAddNote = async (e) => { e.preventDefault(); try { const res = await fetch(`${API_BASE_URL}/clients/${selectedClient.id}/notes`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ text: noteText, isImportant: noteImportant }) }); if (res.ok) { const data = await res.json(); const updatedClients = clients.map(c => { if(c.id === selectedClient.id) { const updatedNotes = [data.note, ...(c.notes || [])]; const updated = { ...c, notes: updatedNotes }; setSelectedClient(updated); return updated; } return c; }); setClients(updatedClients); setNoteText(''); setNoteImportant(false); } } catch (e) {} };
  const handleAddReminder = async (e) => { e.preventDefault(); try { const res = await fetch(`${API_BASE_URL}/clients/${selectedClient.id}/reminders`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ task: reminderTask, dueDate: reminderDate }) }); if (res.ok) { const data = await res.json(); const updatedClients = clients.map(c => { if(c.id === selectedClient.id) { const updatedReminders = [...(c.reminders || []), data.reminder]; updatedReminders.sort((a,b) => new Date(a.dueDate) - new Date(b.dueDate)); const updated = { ...c, reminders: updatedReminders }; setSelectedClient(updated); return updated; } return c; }); setClients(updatedClients); setReminderTask(''); setReminderDate(''); } } catch(e) {} };
  const handleAddPayment = async (e) => { e.preventDefault(); if (!paymentAmount) return; try { const res = await fetch(`${API_BASE_URL}/clients/${selectedClient.id}/payments`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ amount: paymentAmount }) }); if (res.ok) { const data = await res.json(); const updatedClients = clients.map(c => { if(c.id === selectedClient.id) { const updatedPayments = [data.payment, ...(c.payments || [])]; const updated = { ...c, payments: updatedPayments }; setSelectedClient(updated); return updated; } return c; }); setClients(updatedClients); setPaymentAmount(''); } else { alert("Failed"); } } catch (e) { alert("Error"); } };
  const handleApproval = async (status) => { let reason = ''; if(status === 'Rejected') { reason = prompt("Reason:"); if(!reason) return; } try { const res = await fetch(`${API_BASE_URL}/clients/${selectedClient.id}/approval`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ status, reason }) }); if(res.ok) { const data = await res.json(); const updatedClients = clients.map(c => { if(c.id === selectedClient.id) { const updated = { ...c, approvalStatus: status, approvedBy: data.approvedBy, approvedAt: data.approvedAt, rejectionReason: reason || null }; setSelectedClient(updated); return updated; } return c; }); setClients(updatedClients); fetchClients(); } } catch(e) {} };
  const handleSaveStaff = async (e) => { e.preventDefault(); const endpoint = isEditingStaff ? `/users/${staffForm.id}` : '/users'; const method = isEditingStaff ? 'PUT' : 'POST'; try { const res = await fetch(`${API_BASE_URL}${endpoint}`, { method: method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(staffForm) }); if(res.ok) { alert(isEditingStaff ? "Staff updated" : "Staff created"); setStaffForm({ id: null, name: '', email: '', password: '', role: 'Staff' }); setIsEditingStaff(false); fetchUsers(); } } catch(e){} };
  const handleDeleteStaff = async (id) => { if(!window.confirm("Delete user?")) return; try { await fetch(`${API_BASE_URL}/users/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }); fetchUsers(); } catch(e){} };
  const handleEditStaff = (u) => { setStaffForm({ ...u, password: '' }); setIsEditingStaff(true); };

  if (!token || !user) return <LoginView onLogin={handleLogin} onVerify2FA={handleVerify2FA} show2FA={requires2FA} qrCodeUrl={qrCodeUrl} secret={secretKey} loading={loading} error={authError} />;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans text-slate-900">
      <nav className="bg-slate-900 text-white px-6 py-4 flex justify-between items-center shadow-md">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => setView('portal')}>
          <div className="bg-white p-1 rounded-md flex items-center justify-center">
            <img src="/kqsolictor.jpg" alt="Logo" className="w-8 h-8 object-contain" />
          </div>
          <div><h1 className="text-xl font-bold">KQ Solicitor Case Manager</h1>{user.role === 'Admin' && <span className="text-xs bg-red-500 px-1.5 rounded text-white ml-1">ADMIN</span>}</div>
        </div>
        <div className="flex gap-4">
          {user.role === 'Admin' && <button onClick={() => setView('team')} className="flex items-center gap-1 hover:text-slate-300"><UserPlus className="w-4 h-4"/> Team</button>}
          <button onClick={() => setView('settings')} className="flex items-center gap-1 hover:text-slate-300"><Settings className="w-4 h-4"/> Settings</button>
          <button onClick={handleLogout} className="bg-red-600 px-3 py-1 rounded hover:bg-red-700">Logout</button>
        </div>
      </nav>

      <main className="flex-1 max-w-7xl w-full mx-auto p-6">
        
        {/* Upload Progress Overlay */}
        {isUploading && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-xl w-96 text-center">
                    <h3 className="text-lg font-bold mb-2">Uploading File...</h3>
                    <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                        <div className="bg-blue-600 h-4 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }}></div>
                    </div>
                    <p className="text-sm text-gray-600">{uploadProgress}% Complete</p>
                    <p className="text-xs text-red-500 mt-2">Do not close this window.</p>
                </div>
            </div>
        )}

        {/* --- PORTAL HOME VIEW --- */}
        {view === 'portal' && (
          <div className="flex flex-col items-center justify-center h-full pt-10 animate-in fade-in zoom-in duration-300">
             <h2 className="text-3xl font-bold text-slate-800 mb-10">Welcome to Portal</h2>
             <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-5xl">
                
                {/* Card 1: File Open */}
                <div 
                  onClick={() => setView('dashboard')}
                  className="bg-white p-10 rounded-2xl shadow-lg border border-slate-200 hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer flex flex-col items-center gap-6 group"
                >
                   <div className="bg-blue-50 p-6 rounded-full group-hover:bg-blue-600 transition-colors duration-300">
                     <FolderOpen className="w-12 h-12 text-blue-600 group-hover:text-white transition-colors duration-300" />
                   </div>
                   <h3 className="text-xl font-bold text-slate-800 group-hover:text-blue-600">File Open</h3>
                   <p className="text-slate-500 text-center text-sm">Access the main Case Management Dashboard to view and edit client files.</p>
                </div>

                {/* Card 2: Training Video */}
                <a 
                  href="https://drive.google.com/drive/folders/1uYjvEuW4kPFeED_OzCDdtRUeWo7kfHwe?usp=sharing" 
                  target="_blank" 
                  rel="noreferrer"
                  className="bg-white p-10 rounded-2xl shadow-lg border border-slate-200 hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer flex flex-col items-center gap-6 group block"
                >
                   <div className="bg-red-50 p-6 rounded-full group-hover:bg-red-600 transition-colors duration-300">
                     <PlayCircle className="w-12 h-12 text-red-600 group-hover:text-white transition-colors duration-300" />
                   </div>
                   <h3 className="text-xl font-bold text-slate-800 group-hover:text-red-600">Training Video</h3>
                   <p className="text-slate-500 text-center text-sm">Watch tutorials and training materials on our secure drive.</p>
                </a>

                {/* Card 3: Templates */}
                <a 
                  href="https://drive.google.com/drive/folders/13phZQB3abX8Ac7zpLQLGdoFI351-SIWN?usp=sharing" 
                  target="_blank" 
                  rel="noreferrer"
                  className="bg-white p-10 rounded-2xl shadow-lg border border-slate-200 hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer flex flex-col items-center gap-6 group block"
                >
                   <div className="bg-green-50 p-6 rounded-full group-hover:bg-green-600 transition-colors duration-300">
                     <FileText className="w-12 h-12 text-green-600 group-hover:text-white transition-colors duration-300" />
                   </div>
                   <h3 className="text-xl font-bold text-slate-800 group-hover:text-green-600">Templates</h3>
                   <p className="text-slate-500 text-center text-sm">Download standard legal templates and forms for case processing.</p>
                </a>

             </div>
          </div>
        )}

        {view === 'dashboard' && (
          <div className="space-y-6">
            
            {/* Biometric Alerts Widget */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 border-b flex items-center gap-2 font-bold text-slate-700">
                    <User className="w-4 h-4"/> Biometric Reminders
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-red-50 p-3 rounded border border-red-100">
                        <h4 className="text-xs font-bold text-red-700 uppercase mb-2">Overdue</h4>
                        {biometricAlerts.overdue.length > 0 ? (
                            <ul className="space-y-1">
                                {biometricAlerts.overdue.map(c => (
                                    <li key={c.id} onClick={() => {setSelectedClient(c); setActiveTab('Overview'); setView('view-client');}} className="text-sm cursor-pointer hover:underline text-red-900">
                                        {c.name} ({formatDate(c.biometricReminderDate)})
                                    </li>
                                ))}
                            </ul>
                        ) : <span className="text-xs text-red-400">None</span>}
                    </div>
                    <div className="bg-blue-50 p-3 rounded border border-blue-100">
                        <h4 className="text-xs font-bold text-blue-700 uppercase mb-2">Today</h4>
                        {biometricAlerts.dueToday.length > 0 ? (
                            <ul className="space-y-1">
                                {biometricAlerts.dueToday.map(c => (
                                    <li key={c.id} onClick={() => {setSelectedClient(c); setActiveTab('Overview'); setView('view-client');}} className="text-sm cursor-pointer hover:underline text-blue-900">
                                        {c.name}
                                    </li>
                                ))}
                            </ul>
                        ) : <span className="text-xs text-blue-400">None</span>}
                    </div>
                    <div className="bg-green-50 p-3 rounded border border-green-100">
                        <h4 className="text-xs font-bold text-green-700 uppercase mb-2">Next 7 Days</h4>
                        {biometricAlerts.upcoming.length > 0 ? (
                            <ul className="space-y-1">
                                {biometricAlerts.upcoming.map(c => (
                                    <li key={c.id} onClick={() => {setSelectedClient(c); setActiveTab('Overview'); setView('view-client');}} className="text-sm cursor-pointer hover:underline text-green-900">
                                        {c.name} ({formatDate(c.biometricReminderDate)})
                                    </li>
                                ))}
                            </ul>
                        ) : <span className="text-xs text-green-400">None</span>}
                    </div>
                </div>
            </div>

            {/* Dashboard Stats */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-6">
                <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                    <div className="text-xs font-bold text-slate-500 uppercase">Total Files</div>
                    <div className="text-2xl font-bold text-slate-800">{clients.length}</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg shadow-sm border border-purple-100 cursor-pointer hover:bg-purple-100" onClick={() => setStatusFilter('File Opened')}>
                    <div className="text-xs font-bold text-purple-600 uppercase flex items-center gap-1"><FolderOpen className="w-3 h-3"/> File Opened</div>
                    <div className="text-2xl font-bold text-purple-800">{statusCounts.opened}</div>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg shadow-sm border border-amber-100 cursor-pointer hover:bg-amber-100" onClick={() => setStatusFilter('Work in Progress')}>
                    <div className="text-xs font-bold text-amber-600 uppercase flex items-center gap-1"><Clock className="w-3 h-3"/> Work in Progress</div>
                    <div className="text-2xl font-bold text-amber-800">{statusCounts.wip}</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100 cursor-pointer hover:bg-blue-100" onClick={() => setStatusFilter('Awaiting Decision')}>
                    <div className="text-xs font-bold text-blue-600 uppercase flex items-center gap-1"><HelpCircle className="w-3 h-3"/> Awaiting Decision</div>
                    <div className="text-2xl font-bold text-blue-800">{statusCounts.awaiting}</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg shadow-sm border border-green-100 cursor-pointer hover:bg-green-100" onClick={() => setStatusFilter('Decision Received')}>
                    <div className="text-xs font-bold text-green-600 uppercase flex items-center gap-1"><CheckCircle className="w-3 h-3"/> Decision Received</div>
                    <div className="text-2xl font-bold text-green-800">{statusCounts.decision}</div>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 bg-white p-4 rounded-lg shadow-sm mt-6">
              <div className="relative w-full xl:w-64">
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                <input className="pl-10 pr-4 py-2 w-full border rounded-lg" placeholder="Search clients..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
              </div>
              <div className="flex flex-wrap gap-2 w-full xl:w-auto items-center">
                <select className="border p-2 rounded bg-white text-sm" value={filterType} onChange={e => setFilterType(e.target.value)}>
                    <option value="All">All Case Types</option>
                    <option value="Family Law">Family Law</option>
                    <option value="Criminal Law">Criminal Law</option>
                    <option value="Corporate Law">Corporate Law</option>
                    <option value="Property Law">Property Law</option>
                    <option value="Immigration Law">Immigration Law</option>
                </select>
                <select className="border p-2 rounded bg-white text-sm max-w-[200px]" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="All">All Statuses</option>
                    {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 p-1.5 rounded">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <select className="bg-transparent text-sm text-slate-700 font-medium focus:outline-none cursor-pointer" value={dateFilter} onChange={e => setDateFilter(e.target.value)}>
                        <option value="All">All Dates</option>
                        {availableDates.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 p-1.5 rounded">
                    <User className="w-4 h-4 text-slate-500" />
                    <select className="bg-transparent text-sm text-slate-700 font-medium focus:outline-none cursor-pointer" value={enteredByFilter} onChange={e => setEnteredByFilter(e.target.value)}>
                        <option value="All">All Creators</option>
                        {availableCreators.map(creator => <option key={creator} value={creator}>{creator}</option>)}
                    </select>
                </div>
                <div className="flex items-center gap-2 bg-red-50 border border-red-100 p-1.5 rounded">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <select className="bg-transparent text-sm text-red-700 font-medium focus:outline-none cursor-pointer" value={missingDocFilter} onChange={e => setMissingDocFilter(e.target.value)}>
                        <option value="None">Show All (No Filter)</option>
                        <option disabled>--- Show Missing: ---</option>
                        {filterCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                </div>
                <button onClick={() => { resetForm(); setView('add-client'); }} className="bg-slate-800 text-white px-4 py-2 rounded flex items-center gap-2"><Plus className="w-4 h-4"/> New</button>
                <button onClick={handleExport} className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700"><FileSpreadsheet className="w-4 h-4" /></button>
              </div>
            </div>

            {/* Active Filters Display */}
            {(missingDocFilter !== 'None' || statusFilter !== 'All' || dateFilter !== 'All' || enteredByFilter !== 'All') && (
                <div className="bg-slate-100 border border-slate-200 text-slate-800 px-4 py-2 rounded-lg flex items-center gap-2 text-sm mt-2">
                    <Filter className="w-4 h-4" />
                    <span className="font-bold">Active Filters:</span> 
                    {statusFilter !== 'All' && <span className="bg-white px-2 py-0.5 rounded border shadow-sm">{statusFilter}</span>}
                    {dateFilter !== 'All' && <span className="bg-white px-2 py-0.5 rounded border shadow-sm">{dateFilter}</span>}
                    {enteredByFilter !== 'All' && <span className="bg-white px-2 py-0.5 rounded border shadow-sm">By: {enteredByFilter}</span>}
                    {missingDocFilter !== 'None' && <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded border border-red-200 shadow-sm">Missing {missingDocFilter}</span>}
                    <span className="ml-auto text-slate-500">{filteredClients.length} results</span>
                    <button onClick={() => { setMissingDocFilter('None'); setStatusFilter('All'); setDateFilter('All'); setEnteredByFilter('All'); }} className="text-blue-600 underline ml-2">Clear All</button>
                </div>
            )}

            <div className="bg-white rounded-lg shadow-sm overflow-hidden mt-6">
               <div className="mt-4">
                  <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b">
                        <tr>
                            <th className="p-4">#</th>
                            <th className="p-4">Name</th>
                            <th className="p-4">Entered By</th>
                            <th className="p-4">Status</th>
                            <th className="p-4">Approval</th>
                            <th className="p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">{filteredClients.map((c, index) => (
                      <tr key={c.id} className="hover:bg-slate-50">
                        <td className="p-4 font-mono text-slate-500 font-bold">#{clients.length - clients.findIndex(item => item.id === c.id)}</td>
                        <td className="p-4 font-medium">{c.name}</td>
                        <td className="p-4 text-slate-500 text-sm">{c.creatorName || 'System'}</td>
                        <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(c.caseStatus)}`}>{c.caseStatus}</span></td>
                        <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-bold ${c.approvalStatus === 'Approved' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>{c.approvalStatus}</span></td>
                        <td className="p-4 flex gap-2 items-center">
                            {activeViewers[c.id] && activeViewers[c.id].length > 0 && (
                                <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full flex items-center gap-1 animate-pulse border border-green-300">
                                    <span className="w-2 h-2 bg-green-500 rounded-full"></span> 
                                    Viewing: {activeViewers[c.id].join(', ')}
                                </span>
                            )}
                            <button onClick={() => { setSelectedClient(c); setActiveTab('Overview'); setView('view-client'); }} className="p-2 bg-slate-100 rounded hover:bg-slate-200"><Eye className="w-4 h-4 text-slate-600"/></button>
                            <button onClick={() => handleEdit(c)} className="p-2 bg-blue-100 rounded hover:bg-blue-200"><Edit className="w-4 h-4 text-blue-600"/></button>
                            {user.role === 'Admin' && <button onClick={() => handleDelete(c.id)} className="p-2 bg-red-100 rounded hover:bg-red-200"><Trash2 className="w-4 h-4 text-red-600"/></button>}
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                  {filteredClients.length === 0 && <div className="p-8 text-center text-slate-400">No cases found matching criteria.</div>}
               </div>
            </div>
          </div>
        )}

        {view === 'add-client' && (
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-6 border-b pb-2">{isEditing ? 'Edit Case' : 'New Case Entry'}</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                    <input className="border p-2 rounded" placeholder="Full Name" value={formData.name} onChange={e=>setFormData({...formData, name:e.target.value})} required/>
                    <input className="border p-2 rounded" placeholder="Email" value={formData.email} onChange={e=>setFormData({...formData, email:e.target.value})} required/>
                    <input className="border p-2 rounded" placeholder="Phone" value={formData.phone} onChange={e=>setFormData({...formData, phone:e.target.value})} required/>
                    <input className="border p-2 rounded" placeholder="Address" value={formData.address} onChange={e=>setFormData({...formData, address:e.target.value})} required/>
                    <input type="date" className="border p-2 rounded" value={formData.dob} onChange={e=>setFormData({...formData, dob:e.target.value})} required/>
                    <input type="date" className="border p-2 rounded" value={formData.fileDate} onChange={e=>setFormData({...formData, fileDate:e.target.value})} required/>
                    <select className="border p-2 rounded" value={formData.caseType} onChange={e=>setFormData({...formData, caseType:e.target.value})}>{['Family Law', 'Criminal Law', 'Corporate Law', 'Property Law', 'Immigration Law'].map(t=><option key={t} value={t}>{t}</option>)}</select>
                    <select className="border p-2 rounded" value={formData.caseStatus} onChange={e=>setFormData({...formData, caseStatus:e.target.value})}>{statusOptions.map(s=><option key={s} value={s}>{s}</option>)}</select>
                    <input className="border p-2 rounded" placeholder="Fee (£)" type="number" value={formData.feeDecided} onChange={e=>setFormData({...formData, feeDecided:e.target.value})}/>
                </div>
                {!isEditing && <div className="border-2 border-dashed p-6 text-center cursor-pointer relative"><input type="file" multiple className="absolute inset-0 opacity-0" onChange={handleFileAdd}/><p>Upload Initial Files</p><p className="text-xs">{formData.files.length} files selected</p></div>}
                <div className="flex justify-end gap-3"><button type="button" onClick={()=>setView('dashboard')} className="border px-4 py-2 rounded">Cancel</button><button className="bg-slate-800 text-white px-6 py-2 rounded">Save Case</button></div>
            </form>
          </div>
        )}

        {view === 'team' && user.role === 'Admin' && (
          <div className="max-w-4xl mx-auto">
             <button onClick={() => setView('dashboard')} className="mb-4 text-sm text-slate-500 flex items-center gap-1">&larr; Dashboard</button>
            <div className="bg-white rounded-lg shadow-lg p-6 border border-slate-200 mb-6">
                <div className="flex items-center gap-3 mb-6 border-b pb-4"><UserPlus className="w-6 h-6 text-slate-700" /><div><h2 className="text-xl font-bold text-slate-800">{isEditingStaff ? 'Edit User' : 'Add New User'}</h2><p className="text-xs text-slate-500">Manage your staff and admin accounts.</p></div></div>
                <form onSubmit={handleSaveStaff} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4"><div><label className="text-xs font-bold text-slate-500">Name</label><input required className="w-full border p-2 rounded" value={staffForm.name} onChange={e => setStaffForm({...staffForm, name: e.target.value})} /></div><div><label className="text-xs font-bold text-slate-500">Email</label><input required type="email" className="w-full border p-2 rounded" value={staffForm.email} onChange={e => setStaffForm({...staffForm, email: e.target.value})} /></div></div>
                    <div className="grid grid-cols-2 gap-4"><div><label className="text-xs font-bold text-slate-500">Role</label><select className="w-full border p-2 rounded bg-white" value={staffForm.role} onChange={e => setStaffForm({...staffForm, role: e.target.value})}><option value="Staff">Staff</option><option value="Admin">Admin</option></select></div><div><label className="text-xs font-bold text-slate-500">Password {isEditingStaff && '(Leave blank to keep)'}</label><input type="password" className="w-full border p-2 rounded" value={staffForm.password} onChange={e => setStaffForm({...staffForm, password: e.target.value})} required={!isEditingStaff} /></div></div>
                    <div className="flex gap-2 justify-end">{isEditingStaff && <button type="button" onClick={() => { setIsEditingStaff(false); setStaffForm({ id: null, name: '', email: '', password: '', role: 'Staff' }); }} className="border px-4 py-2 rounded">Cancel</button>}<button type="submit" className="bg-slate-800 text-white px-6 py-2 rounded hover:bg-slate-700">{isEditingStaff ? 'Update User' : 'Create User'}</button></div>
                </form>
            </div>
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <table className="w-full text-left"><thead className="bg-slate-50 border-b"><tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4 text-right">Actions</th></tr></thead><tbody className="divide-y">{usersList.map(u => (<tr key={u.id}><td className="p-4 font-medium">{u.name}</td><td className="p-4 text-slate-500">{u.email}</td><td className="p-4"><span className={`text-xs px-2 py-1 rounded font-bold ${u.role === 'Admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{u.role}</span></td><td className="p-4 text-right"><button onClick={() => handleEditStaff(u)} className="p-1 text-blue-600 mr-2"><Edit className="w-4 h-4"/></button>{u.id !== user.id && <button onClick={() => handleDeleteStaff(u.id)} className="p-1 text-red-600"><Trash2 className="w-4 h-4"/></button>}</td></tr>))}</tbody></table>
            </div>
          </div>
        )}

        {view === 'settings' && (
          <div className="max-w-md mx-auto">
             <button onClick={() => setView('dashboard')} className="mb-4 text-sm text-slate-500 flex items-center gap-1">&larr; Dashboard</button>
            <div className="bg-white rounded-lg shadow-lg p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-6"><div className="bg-slate-100 p-2 rounded-full"><Settings className="w-6 h-6 text-slate-700" /></div><div><h2 className="text-xl font-bold text-slate-800">Profile Settings</h2><p className="text-xs text-slate-500">Update your credentials.</p></div></div>
                <form onSubmit={async (e) => { e.preventDefault(); try { await fetch(`${API_BASE_URL}/profile`, { method: 'PUT', headers: {'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`}, body: JSON.stringify(profileForm) }); alert("Updated!"); handleLogout(); } catch(e){ alert("Error"); } }} className="space-y-4">
                    <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Current Password</label><input required type="password" className="w-full border p-2 rounded" value={profileForm.currentPassword} onChange={e => setProfileForm({...profileForm, currentPassword: e.target.value})} /></div>
                    <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">New Password</label><input required type="password" className="w-full border p-2 rounded" value={profileForm.newPassword} onChange={e => setProfileForm({...profileForm, newPassword: e.target.value})} /></div>
                    <button className="w-full bg-slate-800 text-white py-2 rounded">Update Password</button>
                </form>
            </div>
          </div>
        )}

        {view === 'view-client' && selectedClient && (
          <div className="max-w-6xl mx-auto flex gap-6">
            
            {/* LEFT SIDEBAR - NAVIGATION */}
            <div className="w-64 bg-white rounded-lg shadow p-4 h-fit sticky top-6">
                <button onClick={() => setView('dashboard')} className="w-full mb-4 text-sm border border-slate-300 text-slate-600 px-3 py-2 rounded hover:bg-slate-50 flex items-center justify-center gap-2">
                    <ArrowLeft className="w-4 h-4"/> Back to Dashboard
                </button>
                
                <h3 className="font-bold text-slate-700 mb-2 px-2 uppercase text-xs tracking-wider">Case Details</h3>
                <div className="space-y-1 mb-6">
                    <button onClick={() => setActiveTab('Overview')} className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${activeTab === 'Overview' ? 'bg-slate-800 text-white' : 'hover:bg-slate-100 text-slate-600'}`}>
                        <Layout className="w-4 h-4"/> Overview
                    </button>
                    <button onClick={() => setActiveTab('Biometrics')} className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${activeTab === 'Biometrics' ? 'bg-slate-800 text-white' : 'hover:bg-slate-100 text-slate-600'}`}>
                        <User className="w-4 h-4"/> Biometric Details
                    </button>
                    <button onClick={() => setActiveTab('KeyDates')} className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${activeTab === 'KeyDates' ? 'bg-slate-800 text-white' : 'hover:bg-slate-100 text-slate-600'}`}>
                        <CalendarDays className="w-4 h-4"/> Key Dates
                    </button>
                    <button onClick={() => setActiveTab('Fees')} className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${activeTab === 'Fees' ? 'bg-slate-800 text-white' : 'hover:bg-slate-100 text-slate-600'}`}>
                        <DollarSign className="w-4 h-4"/> Fees & Payments
                    </button>
                    <button onClick={() => setActiveTab('Reminders')} className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${activeTab === 'Reminders' ? 'bg-slate-800 text-white' : 'hover:bg-slate-100 text-slate-600'}`}>
                        <ListTodo className="w-4 h-4"/> Case Reminders
                    </button>
                    <button onClick={() => setActiveTab('Notes')} className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${activeTab === 'Notes' ? 'bg-slate-800 text-white' : 'hover:bg-slate-100 text-slate-600'}`}>
                        <MessageSquare className="w-4 h-4"/> Client Notes
                    </button>
                </div>

                <h3 className="font-bold text-slate-700 mb-2 px-2 uppercase text-xs tracking-wider">Case Folders</h3>
                <div className="space-y-1">
                    {docCategories.map(cat => (
                        <button key={cat} onClick={() => setActiveTab(cat)} className={`w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between ${activeTab === cat ? 'bg-slate-800 text-white' : 'hover:bg-slate-100 text-slate-600'}`}>
                            <span className="flex items-center gap-2"><FolderOpen className="w-4 h-4"/> {cat}</span>
                            <span className="bg-white/20 text-xs px-1.5 rounded">{selectedClient.files?.filter(f => (f.category || 'General') === cat).length || 0}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* MAIN CONTENT AREA */}
            <div className="flex-1 space-y-6">
                
                <div className={`p-4 rounded-lg flex justify-between items-center ${selectedClient.approvalStatus === 'Approved' ? 'bg-green-100 border-green-300' : 'bg-orange-100 border-orange-300'}`}>
                    <span className="font-bold text-lg">Status: {selectedClient.approvalStatus}</span>
                    {user.role === 'Admin' && selectedClient.approvalStatus === 'Pending Approval' && (
                        <div className="flex gap-3">
                            <button onClick={() => handleApproval('Rejected')} className="bg-white text-red-600 border px-4 py-2 rounded">Reject</button>
                            <button onClick={() => handleApproval('Approved')} className="bg-green-600 text-white px-4 py-2 rounded">Approve</button>
                        </div>
                    )}
                </div>

                {/* --- TAB: OVERVIEW --- */}
                {activeTab === 'Overview' && (
                    <div className="bg-white rounded-lg shadow p-6 animate-in fade-in duration-300">
                        <div className="flex justify-between items-start border-b pb-4 mb-4">
                            <div><h2 className="text-2xl font-bold">{selectedClient.name}</h2><p className="text-slate-500">Case #{selectedClient.id}</p></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-12 gap-y-4 text-sm">
                            <div className="flex justify-between"><span className="text-slate-500">Email:</span> <span className="font-medium">{selectedClient.email}</span></div>
                            <div className="flex justify-between"><span className="text-slate-500">Phone:</span> <span className="font-medium">{selectedClient.phone}</span></div>
                            <div className="flex justify-between"><span className="text-slate-500">Address:</span> <span className="font-medium text-right w-1/2">{selectedClient.address}</span></div>
                            <div className="flex justify-between"><span className="text-slate-500">Date of Birth:</span> <span className="font-medium">{selectedClient.dob}</span></div>
                            <div className="flex justify-between"><span className="text-slate-500">File Opening Date:</span> <span className="font-medium">{selectedClient.fileDate ? formatDate(selectedClient.fileDate) : 'N/A'}</span></div>
                            <div className="flex justify-between"><span className="text-slate-500">Case Type:</span> <span className="font-medium">{selectedClient.caseType}</span></div>
                            <div className="flex justify-between"><span className="text-slate-500">ID Proof:</span> <span className="font-medium">{selectedClient.idProofType}</span></div>
                            <div className="flex justify-between"><span className="text-slate-500">Addr Proof:</span> <span className="font-medium">{selectedClient.addressProofType}</span></div>
                            <div className="flex justify-between col-span-2 border-t pt-2 mt-2">
                            <span className="text-slate-500">Current Status:</span> 
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${getStatusColor(selectedClient.caseStatus)}`}>{selectedClient.caseStatus}</span>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* --- TAB: BIOMETRIC DETAILS --- */}
                {activeTab === 'Biometrics' && (
                    <div className="bg-white rounded-lg shadow p-6 animate-in fade-in duration-300">
                        <div className="flex justify-between items-center mb-6 border-b pb-4">
                            <div className="flex items-center gap-2 text-slate-800 font-bold text-lg">
                                <User className="w-5 h-5 text-indigo-600"/> Biometric Details
                            </div>
                            <button 
                                onClick={handleBiometricSave} 
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm flex items-center gap-2 shadow-sm transition-colors"
                            >
                                <Save className="w-4 h-4"/> Save Changes
                            </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Biometric Appointment Date</label>
                                <input 
                                    type="date" 
                                    className="w-full border border-slate-300 rounded p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                    value={bioForm.biometricAppointmentDate}
                                    onChange={(e) => setBioForm({...bioForm, biometricAppointmentDate: e.target.value})}
                                />
                            </div>
                            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Reminder Date</label>
                                <input 
                                    type="date" 
                                    className="w-full border border-slate-300 rounded p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                    value={bioForm.biometricReminderDate}
                                    onChange={(e) => setBioForm({...bioForm, biometricReminderDate: e.target.value})}
                                />
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Biometric Notes / Instructions</label>
                            <textarea 
                                rows="3"
                                className="w-full border border-slate-300 rounded p-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
                                placeholder="Enter any specific notes..."
                                value={bioForm.biometricNote}
                                onChange={(e) => setBioForm({...bioForm, biometricNote: e.target.value})}
                            ></textarea>
                        </div>

                        <div className="flex items-center bg-green-50 p-4 rounded-lg border border-green-100">
                             <input 
                                type="checkbox" 
                                id="bioSent"
                                className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                                checked={bioForm.biometricReminderSent}
                                onChange={(e) => setBioForm({...bioForm, biometricReminderSent: e.target.checked})}
                             />
                             <label htmlFor="bioSent" className="ml-2 text-sm font-medium text-green-900 cursor-pointer">
                                Mark reminder as sent to client
                             </label>
                        </div>
                    </div>
                )}

                {/* --- TAB: KEY DATES --- */}
                {activeTab === 'KeyDates' && (
                    <div className="bg-white rounded-lg shadow p-6 animate-in fade-in duration-300">
                        <div className="flex justify-between items-center mb-6 border-b pb-4">
                            <div className="flex items-center gap-2 text-slate-800 font-bold text-lg">
                                <CalendarDays className="w-5 h-5 text-indigo-600"/> Key Dates
                            </div>
                            <div className="flex gap-3">
                                <button onClick={handleDownloadKeyDates} className="bg-white border border-slate-300 text-slate-600 px-4 py-2 rounded text-sm hover:bg-slate-50 transition-colors flex items-center gap-2">
                                    <Download className="w-4 h-4"/> Download
                                </button>
                                <button onClick={handleKeyDatesSave} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm flex items-center gap-2 shadow-sm transition-colors">
                                    <Save className="w-4 h-4"/> Save Changes
                                </button>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 uppercase">Client Visited Date</label>
                                <input type="date" className="w-full border border-slate-300 rounded p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" value={keyDatesForm.keyClientInstructionDate || ''} onChange={(e) => setKeyDatesForm({...keyDatesForm, keyClientInstructionDate: e.target.value})} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 uppercase">Submission Date</label>
                                <input type="date" className="w-full border border-slate-300 rounded p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" value={keyDatesForm.keyAppSubmissionDate || ''} onChange={(e) => setKeyDatesForm({...keyDatesForm, keyAppSubmissionDate: e.target.value})} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 uppercase">Biometric Date</label>
                                <input type="date" className="w-full border border-slate-300 rounded p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" value={keyDatesForm.keyBiometricDate || ''} onChange={(e) => setKeyDatesForm({...keyDatesForm, keyBiometricDate: e.target.value})} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 uppercase">Decision Received Date</label>
                                <input type="date" className="w-full border border-slate-300 rounded p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" value={keyDatesForm.keyDecisionReceivedDate || ''} onChange={(e) => setKeyDatesForm({...keyDatesForm, keyDecisionReceivedDate: e.target.value})} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 uppercase">Decision Sent Date</label>
                                <input type="date" className="w-full border border-slate-300 rounded p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" value={keyDatesForm.keyDecisionSentDate || ''} onChange={(e) => setKeyDatesForm({...keyDatesForm, keyDecisionSentDate: e.target.value})} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 uppercase">File Closing Date</label>
                                <input type="date" className="w-full border border-slate-300 rounded p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" value={keyDatesForm.keyFileClosingDate || ''} onChange={(e) => setKeyDatesForm({...keyDatesForm, keyFileClosingDate: e.target.value})} />
                            </div>
                        </div>
                    </div>
                )}
                
                {/* --- TAB: FEES & PAYMENTS --- */}
                {activeTab === 'Fees' && (
                    <div className="bg-white rounded-lg shadow p-6 animate-in fade-in duration-300">
                        <div className="flex items-center gap-2 mb-4 border-b pb-2">
                            <DollarSign className="w-5 h-5 text-slate-600"/>
                            <h3 className="font-bold text-lg text-slate-800">Fees & Payments</h3>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 mb-6">
                            <div className="bg-slate-50 p-3 rounded border">
                                <p className="text-xs text-slate-500 uppercase font-bold">Total Fee</p>
                                <p className="text-xl font-bold text-slate-800">£{selectedClient.feeDecided || 0}</p>
                            </div>
                            <div className="bg-green-50 p-3 rounded border border-green-100">
                                <p className="text-xs text-green-600 uppercase font-bold">Total Paid</p>
                                <p className="text-xl font-bold text-green-700">£{totalPaid}</p>
                            </div>
                            <div className="bg-red-50 p-3 rounded border border-red-100">
                                <p className="text-xs text-red-600 uppercase font-bold">Balance Due</p>
                                <p className="text-xl font-bold text-red-700">£{(selectedClient.feeDecided || 0) - totalPaid}</p>
                            </div>
                        </div>

                        <form onSubmit={handleAddPayment} className="mb-4 flex gap-2 items-end">
                            <div className="flex-1">
                                <label className="text-xs font-bold text-slate-500">Record New Payment</label>
                                <input type="number" className="w-full border p-2 rounded text-sm mt-1" placeholder="Amount (£)..." value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} required />
                            </div>
                            <button className="bg-green-600 text-white text-xs px-4 py-2.5 rounded hover:bg-green-700 h-fit">Record Payment</button>
                        </form>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-slate-50 text-slate-500">
                                    <tr><th className="p-2">Date & Time</th><th className="p-2">Amount</th><th className="p-2">Received By</th></tr>
                                </thead>
                                <tbody className="divide-y">
                                    {selectedClient.payments?.map((p, i) => (
                                        <tr key={i}>
                                            <td className="p-2">{new Date(p.date).toLocaleString()}</td>
                                            <td className="p-2 font-bold text-green-700">£{p.amount}</td>
                                            <td className="p-2">{p.receivedBy}</td>
                                        </tr>
                                    ))}
                                    {(!selectedClient.payments || selectedClient.payments.length === 0) && (
                                        <tr><td colSpan="3" className="p-4 text-center text-slate-400">No payments recorded.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* --- TAB: REMINDERS --- */}
                {activeTab === 'Reminders' && (
                    <div className="bg-white rounded-lg shadow p-6 animate-in fade-in duration-300">
                        <div className="flex items-center gap-2 border-b pb-2 mb-4">
                            <Bell className="w-5 h-5 text-slate-600"/>
                            <h3 className="font-bold text-lg text-slate-800">Case Reminders</h3>
                        </div>
                        <form onSubmit={handleAddReminder} className="mb-4 flex gap-2">
                            <input className="border p-2 rounded text-sm flex-1" placeholder="Task..." value={reminderTask} onChange={e => setReminderTask(e.target.value)} required />
                            <input type="date" className="border p-2 rounded text-sm" value={reminderDate} onChange={e => setReminderDate(e.target.value)} required />
                            <button className="bg-slate-800 text-white text-xs px-4 py-2 rounded">Set</button>
                        </form>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                            {selectedClient.reminders?.map((r, i) => (
                                <div key={i} className="flex justify-between bg-slate-50 p-2 rounded border text-sm">
                                    <span>{r.task}</span>
                                    <span className="text-red-500 font-bold">{formatDate(r.dueDate)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* --- TAB: NOTES --- */}
                {activeTab === 'Notes' && (
                    <div className="bg-white rounded-lg shadow p-6 animate-in fade-in duration-300">
                        <div className="flex items-center justify-between mb-4 border-b pb-2">
                            <div className="flex items-center gap-2">
                                <MessageSquare className="w-5 h-5 text-slate-600"/>
                                <h3 className="font-bold text-lg text-slate-800">Client Notes</h3>
                                <button onClick={handleDownloadNotes} className="text-slate-400 hover:text-blue-600 ml-2" title="Download Notes">
                                    <Download className="w-4 h-4"/>
                                </button>
                            </div>
                            <div className="relative w-64">
                                <Search className="absolute left-2 top-2 w-4 h-4 text-slate-400" />
                                <input className="pl-8 pr-2 py-1.5 text-sm border rounded w-full focus:outline-none" placeholder="Search notes..." value={noteSearch} onChange={e => setNoteSearch(e.target.value)} />
                            </div>
                        </div>
                        <form onSubmit={handleAddNote} className="mb-4">
                            <textarea className="w-full border p-2 rounded text-sm mb-2" rows="2" placeholder="Note..." value={noteText} onChange={e => setNoteText(e.target.value)} required />
                            <div className="flex justify-between">
                                <label className="text-sm flex gap-2"><input type="checkbox" checked={noteImportant} onChange={e => setNoteImportant(e.target.checked)}/> Important</label>
                                <button className="bg-slate-800 text-white px-4 py-2 rounded text-xs">Add</button>
                            </div>
                        </form>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {selectedClient.notes?.filter(n=>n.text.toLowerCase().includes(noteSearch.toLowerCase())).map(n => (
                                <div key={n.id} className={`p-3 rounded border text-sm mb-2 ${n.isImportant ? 'bg-red-50 border-red-200' : 'bg-slate-50'}`}>
                                    <div className="flex justify-between items-center mb-1 text-xs text-slate-500 border-b border-slate-200/50 pb-1">
                                        <span className="font-bold text-slate-700 flex items-center gap-1"><User className="w-3 h-3"/> {n.author}</span>
                                        <span>{formatDateTime(n.date)}</span>
                                    </div>
                                    <div className="text-slate-800 mt-1">{n.text}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* --- TAB: DOCUMENTS (Dynamic Category) --- */}
                {docCategories.includes(activeTab) && (
                    <div className="bg-white rounded-lg shadow p-6 animate-in fade-in duration-300">
                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><FolderOpen className="w-5 h-5"/> {activeTab} Documents</h3>
                        <div className="relative inline-block mb-4">
                            <button className="bg-slate-800 text-white text-sm px-3 py-1.5 rounded flex items-center gap-1"><Plus className="w-4 h-4"/> Upload</button>
                            <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleSingleUpload} />
                        </div>
                        <div className="space-y-2">
                            {selectedClient.files?.filter(f => (f.category || 'General') === activeTab).map((f, i) => (
                                <div key={i} className="flex justify-between border p-2 rounded text-sm items-center">
                                    <div className="flex items-center gap-2">
                                        <File className="w-4 h-4 text-slate-400"/> {f.name}
                                        {f.uploadedBy && <span className="text-xs text-gray-400 italic ml-2">(Uploaded by: {f.uploadedBy})</span>}
                                    </div>
                                    <div className="flex gap-4 items-center">
                                        {/* NEW VIEW OPTION */}
                                        <button 
                                            onClick={() => window.open(f.path, '_blank')} 
                                            className="text-indigo-600 flex items-center gap-1 hover:underline font-semibold"
                                        >
                                            <Eye className="w-4 h-4" /> View
                                        </button>
                                        <a href={f.path} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Download</a>
                                        <button onClick={() => handleDeleteFile(f.filename)} className="text-red-500 hover:text-red-700 transition-colors">
                                            <Trash2 className="w-4 h-4"/>
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {(!selectedClient.files || selectedClient.files.filter(f => (f.category || 'General') === activeTab).length === 0) && (
                                <p className="text-slate-400 text-sm italic">No documents in this folder.</p>
                            )}
                        </div>
                    </div>
                )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
